/** A failed API call, carrying the server's stable code and field-level details. */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code: string,
    readonly details?: { path: string; message: string }[],
    readonly requestId?: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

type AuthHooks = {
  token: () => string | null;
  /** Trades the refresh cookie for a new access token; null when the session is gone. */
  refresh: () => Promise<string | null>;
  signedOut: () => void;
};

let auth: AuthHooks | null = null;
export const configureAuth = (hooks: AuthHooks) => {
  auth = hooks;
};

async function send<T>(method: string, path: string, body?: unknown, retried = false): Promise<T> {
  const headers: Record<string, string> = { accept: "application/json" };
  if (body !== undefined) headers["content-type"] = "application/json";
  const token = auth?.token();
  if (token) headers.authorization = `Bearer ${token}`;

  const res = await fetch(`/api/v1${path}`, {
    method,
    headers,
    credentials: "same-origin",
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  const json = (await res.json().catch(() => null)) as { data?: T; code?: string; message?: string; details?: { path: string; message: string }[]; requestId?: string } | null;
  if (res.ok) return json?.data as T;

  const code = json?.code ?? "HTTP_ERROR";
  if (res.status === 401 && auth && !path.startsWith("/auth/")) {
    // An expired access token is routine: refresh once and repeat the call.
    if (!retried && (code === "TOKEN_EXPIRED" || code === "TOKEN_INVALID")) {
      const next = await auth.refresh();
      if (next) return send<T>(method, path, body, true);
    }
    auth.signedOut();
  }
  throw new ApiError(json?.message ?? `The request failed (HTTP ${res.status})`, res.status, code, json?.details, json?.requestId);
}

export const api = {
  get: <T>(path: string) => send<T>("GET", path),
  post: <T>(path: string, body: unknown = {}) => send<T>("POST", path, body),
  patch: <T>(path: string, body: unknown) => send<T>("PATCH", path, body),
  put: <T>(path: string, body: unknown) => send<T>("PUT", path, body),
  del: <T>(path: string) => send<T>("DELETE", path),
};

export function query(params: Record<string, string | number | boolean | null | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== "") search.set(key, String(value));
  }
  const text = search.toString();
  return text ? `?${text}` : "";
}

/** One line a person can act on. */
export function errorText(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.details?.length) return `${err.message}: ${err.details.map((d) => `${d.path} ${d.message}`).join("; ")}`;
    return err.message;
  }
  return err instanceof Error ? err.message : "Something went wrong";
}
